// Modal para solicitar visitas con integración de Calendly

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Calendar, ExternalLink } from 'lucide-react';

/**
 * Modal para agendar visitas a propiedades usando Calendly
 * ✅ SIMPLIFICADO: La validación y creación de perfil se maneja en PropertyContent
 * 
 * @param {Object} property - Datos de la propiedad
 * @param {Object} invite - Datos de la invitación (con email y token)
 * @param {Object} buyerProfile - Perfil del comprador (puede ser null)
 * @param {Function} onProfileCreated - Callback cuando se crea el perfil
 * @param {boolean} isOpen - Estado de apertura del modal
 * @param {Function} onClose - Función para cerrar el modal
 * @param {Function} trackEvent - Función de tracking de analytics
 */
const ScheduleVisitModal = ({ 
  property, 
  invite,
  buyerProfile,
  onProfileCreated,
  isOpen, 
  onClose, 
  trackEvent 
}) => {
  const { t } = useTranslation();
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const [error, setError] = useState(false);

  // URL de calendario configurada en la propiedad
  const CALENDLY_URL = property?.calendlyUrl;
  const hasCalendlyUrl = !!CALENDLY_URL;

  useEffect(() => {
    if (isOpen) {
      // ✅ BLOQUEAR SCROLL DEL BODY cuando modal está abierto
      document.body.style.overflow = 'hidden';

      // Track apertura del modal
      trackEvent?.('schedule_visit_opened', {
        propertyId: property?.id,
        propertyTitle: property?.title,
        propertyStatus: property?.status,
        hasProfile: !!buyerProfile,
        hasCalendlyUrl: hasCalendlyUrl,
        timestamp: new Date().toISOString(),
      });
      
      // Resetear estados para mostrar Calendly
      setCalendlyLoaded(false);
      setError(false);
    }

    // ✅ RESTAURAR SCROLL AL CERRAR modal (cleanup)
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, property, trackEvent, buyerProfile, hasCalendlyUrl]);

  const handleIframeLoad = () => {
    setCalendlyLoaded(true);
    setError(false);
    // Nota: No trackeamos carga técnica del iframe - solo eventos de engagement
  };

  const handleIframeError = () => {
    setError(true);
    // Nota: Errores técnicos van a logs, no a engagement events
    console.error('Error loading Calendly iframe for property:', property?.id);
  };

  const handleClose = () => {
    // Track cierre del modal
    trackEvent?.('schedule_visit_closed', {
      propertyId: property?.id,
      calendlyLoaded,
      hadError: error,
      timestamp: new Date().toISOString(),
    });

    onClose();
  };

  const handleOpenInNewTab = () => {
    // Track apertura en nueva pestaña
    trackEvent?.('schedule_visit_external', {
      propertyId: property?.id,
      timestamp: new Date().toISOString(),
    });

    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  // ============================================
  // MODAL DE CALENDLY - OPTIMIZADO PARA MÓVIL
  // ============================================
  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal Container - Fullscreen móvil/tablet, centrado desktop */}
      <div className="fixed inset-0 lg:flex lg:items-center lg:justify-center lg:p-4">
        
        {/* Modal Content */}
        <div className="relative bg-slate-800 w-full h-full flex flex-col border-slate-700 lg:rounded-2xl lg:shadow-2xl lg:max-w-5xl lg:h-[95vh] lg:border">
          
          {/* Header - Compacto */}
          <div className="flex items-center justify-between p-3 lg:p-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Calendar className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <h2 className="text-base lg:text-lg font-bold text-white truncate">
                {t('visit.modalTitle')}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Body - Calendly iframe con estados de carga/error */}
          <div className="flex-1 relative overflow-hidden">
            
            {/* Loading state */}
            {!calendlyLoaded && !error && hasCalendlyUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 z-10">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
                  <p className="text-white">{t('visit.loading')}</p>
                </div>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('visit.error')}</h3>
                  <p className="text-slate-400 mb-4">
                    {t('visit.errorDesc')}
                  </p>
                  <button
                    onClick={handleClose}
                    className="bg-slate-700 text-white px-6 py-2 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            )}

            {/* No Calendly URL */}
            {!hasCalendlyUrl && (
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('visit.notConfigured')}</h3>
                  <p className="text-slate-400">
                    {t('visit.notConfiguredDesc')}
                  </p>
                </div>
              </div>
            )}

            {/* Calendly Iframe - SIN doble scroll */}
            {hasCalendlyUrl && !error && (
              <iframe
                src={CALENDLY_URL}
                width="100%"
                height="100%"
                frameBorder="0"
                className="w-full h-full"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                title={t('visit.iframeTitle')}
                allow="payment"
              />
            )}
          </div>

          {/* Footer - Compacto */}
          <div className="p-2 lg:p-3 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-slate-300 truncate">
                {t('visit.selectDateTime')}
              </p>
              {hasCalendlyUrl && (
                <button
                  onClick={handleOpenInNewTab}
                  className="text-sm text-amber-500 hover:text-amber-400 transition-colors flex items-center gap-1 flex-shrink-0"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('visit.openNewTab')}</span>
                  <span className="sm:hidden">{t('visit.newTab')}</span>
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ScheduleVisitModal;